import {Component, computed, inject, input, output, signal} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {Dialog} from 'primeng/dialog';
import {
  ParticipatingToolControllerService,
  TenderPhaseDetailsResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {firstValueFrom} from 'rxjs';
import {ModelFieldDatepickerComponent} from '../../../../inputs/model-field/model-field-datepicker/model-field-datepicker.component';
import {
  addDays,
  createLocalDate,
  parseLocalDate,
  toLocalDateString,
} from '../../../../../utils/date-utils';

const MIN_DAYS_UNTIL_SUBMISSION = 7;
const MAX_DAYS_UNTIL_SUBMISSION = 28;

@Component({
  selector: 'app-send-invitation-button',
  imports: [TranslocoPipe, Button, Dialog, ModelFieldDatepickerComponent],
  templateUrl: './send-invitation-button.component.html',
})
export class SendInvitationButtonComponent {
  private readonly participatingToolService = inject(ParticipatingToolControllerService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);

  readonly projectId = input.required<number>();
  readonly currentPhase = input.required<TenderPhaseDetailsResponse | null>();
  readonly suggestedSubmissionDate = input.required<string | null>();
  readonly hasUninvitedParticipants = input.required<boolean>();

  readonly invited = output<void>();

  protected readonly dialogVisible = signal(false);
  protected readonly selectedDate = signal<Date | null>(null);
  protected readonly sending = signal(false);

  protected readonly phase = computed(() => this.currentPhase()?.phase);

  private readonly lastSubmissionDate = computed(() => {
    const phase = this.currentPhase();
    const lastDate =
      phase?.finalSubPhase?.submissionDate ??
      phase?.extendedSubPhase?.submissionDate ??
      phase?.initialSubPhase?.submissionDate;
    return lastDate ? parseLocalDate(lastDate) : createLocalDate();
  });

  protected readonly submissionDateMin = computed(() =>
    addDays(createLocalDate(), MIN_DAYS_UNTIL_SUBMISSION),
  );
  protected readonly submissionDateMax = computed(() =>
    addDays(this.lastSubmissionDate(), MAX_DAYS_UNTIL_SUBMISSION),
  );
  protected readonly isFirstInvitation = computed(() => {
    const currentPhase = this.currentPhase();
    return currentPhase ? !currentPhase.initialSubPhase?.shippingDate : true;
  });

  protected openDialog(): void {
    this.selectedDate.set(this.prefillSubmissionDate());
    this.dialogVisible.set(true);
  }

  protected closeDialog(): void {
    this.dialogVisible.set(false);
  }

  protected async confirmSend(): Promise<void> {
    const submissionDate = this.selectedDate();
    const phase = this.phase();
    if (!submissionDate || !phase) {
      return;
    }
    this.dialogVisible.set(false);
    await this.sendInvitations(phase, submissionDate);
  }

  private async sendInvitations(phase: TenderPhaseType, submissionDate: Date): Promise<void> {
    this.sending.set(true);

    try {
      await firstValueFrom(
        this.participatingToolService.inviteTools({
          projectId: this.projectId(),
          phase,
          inviteToolsRequest: {submissionDate: toLocalDateString(submissionDate)},
        }),
      );

      this.invited.emit();

      const today = new Intl.DateTimeFormat(this.transloco.getActiveLang()).format(new Date());
      await this.toast.showSuccess(
        'common.success',
        'project.steps.invitationList.successToast.messageWithDate',
        {date: today},
      );
    } finally {
      this.sending.set(false);
    }
  }

  private prefillSubmissionDate(): Date {
    const suggested = this.suggestedSubmissionDate();
    const min = this.submissionDateMin();
    const max = this.submissionDateMax();
    return this.clampToWindow(suggested ? new Date(suggested) : min, min, max);
  }

  private clampToWindow(date: Date, min: Date, max: Date): Date {
    const day = parseLocalDate(date);

    if (day.getTime() < min.getTime()) {
      return min;
    }
    if (day.getTime() > max.getTime()) {
      return max;
    }
    return day;
  }
}
